// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import ProductAttributes, { ProductAttributesStyles } from "@stoneage/Overrides/Components/ProductAttributes";
import ConfigureProductButton from "@stoneage/App/Components/ProductCards/ConfigureProductButton";
import CustomProductPrice, { CustomProductPriceStyles } from "@stoneage/App/Components/ProductCards/CustomProductPrice";
import { useVisibleChildCount } from "@stoneage/App/Hooks/useVisibleChildCount";
import { xIsAuthenticated } from "@stoneage/App/Store/Selectors/GeneralSelectors";
import { xClassNames } from "@stoneage/XDL/Utilities";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    // XNG-Changes
    showAvailability?: boolean;
    showAttributes?: boolean;
    relatedProductsEventHandler?: (destination: string) => void;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes
    container?: InjectableCss;
    imageWrapper?: InjectableCss;
    attributesContainer?: InjectableCss;
    attributesStyles?: ProductAttributesStyles;
    partNumbersContainer?: InjectableCss;
    unitOfMeasureWrapper?: InjectableCss;
    customPriceStyles?: CustomProductPriceStyles;
    clearContainer?: InjectableCss;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    // XNG-Changes
    isAuthenticated,
    showAvailability,
    showAttributes,
    actionsTemplate,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    // XNG-Changes
    const { wrapperRef, visibleCount } = useVisibleChildCount(6);

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    // const {
    //     showOverlayBadge,
    //     overlayBadge,
    //     overlayBadgeWrapperStyles,
    //     badgeAreaWrapperRef,
    //     overlayBadgeWrapperClickHandler,
    // } = useOverlayBadge({
    //     productContext,
    //     showTextBadge: showTextBadges,
    //     showImageBadge: showImageBadges,
    //     styles,
    // });
    //
    // const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper {...styles.container} ref={wrapperRef} {...{ gridRowSpan: visibleCount }}>
                {showImage && (
                    <StyledWrapper {...styles.imageWrapper}>
                        <ProductImage
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productImageStyles}
                        />
                    </StyledWrapper>
                )}
                {/* {showImage && showOverlayBadge && overlayBadge && ( */}
                {/*    <StyledWrapper */}
                {/*        {...styles.badgeAreaWrapper} */}
                {/*        ref={badgeAreaWrapperRef} */}
                {/*        data-test-selector="productCarouselBadge" */}
                {/*    > */}
                {/*        <ProductImage extendedStyles={styles.productImageStyles} product={productContext} /> */}
                {/*        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}> */}
                {/*            <Badge */}
                {/*                type={overlayBadge.badgeType} */}
                {/*                value={ */}
                {/*                    overlayBadge.badgeType === "Image" */}
                {/*                        ? overlayBadge.largeImageBadgePath */}
                {/*                        : overlayBadge.displayText */}
                {/*                } */}
                {/*                badgeTextColor={overlayBadge.textColorHexCode} */}
                {/*                badgeBackgroundColor={overlayBadge.badgeColorHexCode} */}
                {/*                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0} */}
                {/*                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name} */}
                {/*                textBadgeCss={styles.badgeText} */}
                {/*                imageBadgeProps={styles.badgeImageProps} */}
                {/*            /> */}
                {/*        </StyledWrapper> */}
                {/*    </StyledWrapper> */}
                {/* )} */}
                {showBrand && product.brand && (
                    <StyledWrapper {...styles.productBrandWrapper}>
                        <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                    </StyledWrapper>
                )}
                {showTitle && (
                    <ProductDescription
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productDescriptionStyles}
                    />
                )}
                {showAttributes && (
                    <StyledWrapper {...styles.attributesContainer}>
                        <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributesStyles} />
                    </StyledWrapper>
                )}
                {showPartNumbers && (
                    <StyledWrapper
                        {...styles.partNumbersContainer}
                        className={xClassNames.append(isAuthenticated && "divider")}
                    >
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            showCustomerName={false}
                            showManufacturerItem={false}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    </StyledWrapper>
                )}
                {/* {showTextBadges && textBadges && ( */}
                {/*    <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} /> */}
                {/* )} */}
                {/* {showImageBadges && imageBadges && ( */}
                {/*    <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} /> */}
                {/* )} */}
                {showPrice && (
                    <>
                        <CustomProductPrice
                            showAvailability={showAvailability}
                            showPrice={showPrice}
                            extendedStyles={styles.customPriceStyles}
                        />
                        <StyledWrapper {...styles.unitOfMeasureWrapper}>
                            <ProductUnitOfMeasureSelect extendedStyles={styles.productUnitOfMeasureSelectStyles} />
                        </StyledWrapper>
                    </>
                    // <StyledWrapper {...styles.productPriceWrapper}>
                    //     <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                    //         {translate("U/M")}
                    //     </VisuallyHidden>
                    //     <ProductPrice showLabel={false} product={productContext} />
                    //     {product.unitOfMeasures && (
                    //         <ProductUnitOfMeasureSelect
                    //             labelOverride=""
                    //             extendedStyles={styles.productUnitOfMeasureSelectStyles}
                    //             id={`${carouselId}-uom`}
                    //         />
                    //     )}
                    //     <ProductQuantityBreakPricing extendedStyles={styles.productQuantityBreakPricingStyles} />
                    // </StyledWrapper>
                )}
                {showAddToCart && (
                    <StyledWrapper {...styles.addToCartContainer}>
                        <ConfigureProductButton />
                    </StyledWrapper>
                )}
                {/* {showAddToCart && ( */}
                {/*    <StyledWrapper {...styles.addToCartContainer}> */}
                {/*        <StyledWrapper {...styles.qtyGridItem}> */}
                {/*            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}> */}
                {/*                {translate("QTY")} */}
                {/*            </VisuallyHidden> */}
                {/*            <ProductQuantityOrdered */}
                {/*                labelOverride="" */}
                {/*                extendedStyles={styles.productQuantityOrderedStyles} */}
                {/*                id={`${carouselId}-qty`} */}
                {/*            /> */}
                {/*        </StyledWrapper> */}
                {/*        <StyledWrapper {...styles.addToCartButtonGridItem}> */}
                {/*            <ProductAddToCartButton */}
                {/*                onAddToCartClick={() => trackProductRecommendation(false)} */}
                {/*                data-test-selector="addToCartBtn" */}
                {/*                extendedStyles={styles.productAddToCartButtonStyles} */}
                {/*            /> */}
                {/*        </StyledWrapper> */}
                {/*    </StyledWrapper> */}
                {/* )} */}
                {/* {showAddToList && ( */}
                {/*    <ProductAddToListLink */}
                {/*        data-test-selector="addToListLink" */}
                {/*        extendedStyles={styles.productAddToListLinkStyles} */}
                {/*    /> */}
                {/* )} */}
                {actionsTemplate && (
                    <StyledWrapper {...styles.clearContainer}>{actionsTemplate(product)}</StyledWrapper>
                )}
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
